import React from 'react';
import PropTypes from 'prop-types';
import { formatPrice, formatKRW, formatCurrency } from '../../utils/format';
import { useTranslation } from '../../redux/TranslationHelper';

/**
 * 기본 가격 표시 컴포넌트
 * @param {number|string} price - 표시할 가격
 * @param {boolean} includeSymbol - 통화 기호 포함 여부
 * @param {string} symbol - 통화 기호
 * @param {string} className - 추가 CSS 클래스
 */
export const PriceDisplay = ({ 
  price, 
  includeSymbol = true, 
  symbol = '₩', 
  className = '',
  ...rest
}) => {
  const { currentLanguage } = useTranslation();
  
  return (
    <span className={`price-display ${className}`} {...rest}>
      {includeSymbol ? (symbol === '₩' ? formatKRW(price) : formatCurrency(price, currentLanguage)) : formatPrice(price)}
    </span>
  );
};

/**
 * 할인 가격 표시 컴포넌트
 * @param {number|string} originalPrice - 원래 가격
 * @param {number|string} discountedPrice - 할인된 가격
 * @param {string} originalPriceClassName - 원래 가격의 CSS 클래스
 * @param {string} discountedPriceClassName - 할인 가격의 CSS 클래스
 * @param {JSX.Element} separator - 두 가격 사이의 구분자
 */
export const DiscountedPriceDisplay = ({
  originalPrice,
  discountedPrice,
  originalPriceClassName = 'old-price',
  discountedPriceClassName = 'new-price',
  separator = ' ',
  symbol = '₩',
  ...rest
}) => {
  const { currentLanguage } = useTranslation();
  
  return (
    <div className="discounted-price-display" {...rest}>
      <span className={discountedPriceClassName}>
        {symbol === '₩' ? formatKRW(discountedPrice) : formatCurrency(discountedPrice, currentLanguage)}
      </span>
      {separator}
      <span className={originalPriceClassName}>
        {symbol === '₩' ? formatKRW(originalPrice) : formatCurrency(originalPrice, currentLanguage)}
      </span>
    </div>
  );
};

/**
 * 할인율과 함께 표시하는 컴포넌트
 */
export const DiscountRatePriceDisplay = ({
  originalPrice,
  discountedPrice,
  originalPriceClassName = 'old-price',
  discountedPriceClassName = 'new-price',
  discountRateClassName = 'discount-rate',
  separator = ' ',
  symbol = '₩',
  ...rest
}) => {
  // 다국어 지원 훅 사용
  const { t, currentLanguage } = useTranslation();
  
  // 할인율 계산 (백분율)
  const calculateDiscountRate = () => {
    if (!originalPrice || !discountedPrice || originalPrice <= 0) return 0;
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  };
  
  const discountRate = calculateDiscountRate();
  
  return (
    <div className="discount-rate-price-display" {...rest}>
      <span className={discountedPriceClassName}>
        {symbol === '₩' ? formatKRW(discountedPrice) : formatCurrency(discountedPrice, currentLanguage)}
      </span>
      {separator}
      <span className={originalPriceClassName}>
        {symbol === '₩' ? formatKRW(originalPrice) : formatCurrency(originalPrice, currentLanguage)}
      </span>
      {discountRate > 0 && (
        <span className={discountRateClassName}>{discountRate}% {t('할인')}</span>
      )}
    </div>
  );
};

PriceDisplay.propTypes = {
  price: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  includeSymbol: PropTypes.bool,
  symbol: PropTypes.string,
  className: PropTypes.string
};

DiscountedPriceDisplay.propTypes = {
  originalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  discountedPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  originalPriceClassName: PropTypes.string,
  discountedPriceClassName: PropTypes.string,
  separator: PropTypes.node,
  symbol: PropTypes.string
};

DiscountRatePriceDisplay.propTypes = {
  originalPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  discountedPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  originalPriceClassName: PropTypes.string,
  discountedPriceClassName: PropTypes.string,
  discountRateClassName: PropTypes.string,
  separator: PropTypes.node,
  symbol: PropTypes.string
};

export default PriceDisplay; 